import React, { Component } from 'react';
import styled from 'styled-components';
import { changeLocation } from 'api/Location';

import Auth from '../../../routes/Auth';
import {
  getRequest,
  postRequest,
  patchRequest,
} from '../../../api/ApiService';
import { clearAppGeneralInfoCache } from '../../../api/AppGeneralInfoService';

import OnboardingLayout from './OnboardingLayout';

const API = process.env.REACT_APP_API_URL;

// Hardcoded question text lists. Resolved to backend question IDs at
// runtime by matching normalized text against /v2/forms/questions/.
const INITIAL_STAGE_QUESTIONS = [
  "What's working well within your team? What could work better?",
  "Besides yourself, who has been the most valuable person on your team?",
  "This team member RECOGNIZED other team members' contributions.",
  "This team member was RESPONSIVE to team members' communication within 24hrs.",
  "This team member has DEMONSTRATED that they are listening to others.",
  "This team member DID NOT DOMINATE discussions and DID NOT MAKE IT DIFFICULT for others to share their viewpoints.",
  "This team member CONTRIBUTED to the team.",
  "Would you want to work with this teammate again?",
  "I Like: In terms of working together, what did you like about <Insert User FirstName>? Provide examples if possible.",
  "I Wish: In terms of working together,  what do you wish <insert user firstname> could have done more of? Provide examples.",
];

const FINAL_STAGE_QUESTIONS = [
  "What were your biggest takeaways from this exercise? What did you learn about yourself, working in teams, and business in general?",
  "If you had a chance to do it all over again, what would you have done differently?",
  "Besides yourself, who has been the most valuable person on your team?",
  "This team member RECOGNIZED other team members' contributions.",
  "This team member was RESPONSIVE to team members' communication within 24hrs.",
  "This team member has DEMONSTRATED that they are listening to others.",
  "This team member DID NOT DOMINATE discussions and DID NOT MAKE IT DIFFICULT for others to share their viewpoints.",
  "This team member CONTRIBUTED to the team.",
  "Would you want to work with this teammate again?",
  "I Like: In terms of working together, what did you like about <Insert User FirstName>? Provide examples if possible.",
  "I Wish: In terms of working together,  what do you wish <insert user firstname> could have done more of? Provide examples.",
];

// Lenient normalizer: lowercase, strip punctuation/whitespace variants,
// collapse runs of whitespace. Keeps the FE resilient to small typos or
// punctuation drift between our hardcoded copy and the backend's stored
// question text.
const normalizeQuestionText = (s) => (s || '')
  .toLowerCase()
  .replace(/[^\w]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const Heading = styled.h1`
  margin: 0 0 30px;
  font-family: 'Mukta Mahee', 'Mukta Vaani', sans-serif;
  font-size: 24px;
  line-height: 60px;
  font-weight: 700;
  color: #2F4166;
  text-align: center;
`;

const InfoBox = styled.div`
  background: #FFFFFF;
  border: 1px solid #55B4BC;
  border-radius: 4px;
  padding: 22px 20px;
  margin-bottom: 24px;
  font-family: 'Mukta Mahee', 'Mukta Vaani', sans-serif;
  font-size: 16px;
  line-height: 24px;
  color: #2F4166;

  strong { font-weight: 700; }
`;

const RadioRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  background: #FFFFFF;
  border: 2px solid transparent;
  border-radius: 10px;
  padding: 14px 20px;
  margin-bottom: 16px;
  cursor: pointer;
  transition: border-color 160ms ease, box-shadow 160ms ease;
  box-shadow: 0 4px 4px #E2E2E2;

  ${p => p.selected && `
    border-color: #2F4166;
    box-shadow: 0 4px 12px rgba(47, 65, 102, 0.15);
  `}

  &:hover { border-color: ${p => p.selected ? '#2F4166' : 'rgba(47, 65, 102, 0.3)'}; }
`;

const RadioLabel = styled.div`
  font-family: 'Mukta Mahee', 'Mukta Vaani', sans-serif;
  font-size: 16px;
  line-height: 22px;
  color: rgba(0, 27, 59, 0.6);

  strong {
    display: inline-block;
    color: #2F4166;
    font-weight: 700;
    font-size: 18px;
    margin-right: 12px;
  }
`;

class TeamEvaluationSurvey extends Component {
  state = { selected: null, submitting: false };

  handleSelect = (value) => {
    if (this.state.submitting) return;
    this.setState({ selected: value });
  };

  handleContinue = () => {
    const { selected, submitting } = this.state;
    if (!selected || submitting) return;

    this.setState({ submitting: true });

    this.fetchCourseGameType()
      .then(gameType => {
        const surveyWork = selected === 'yes'
          ? this.createSurveys(gameType)
          : Promise.resolve();
        return surveyWork.then(() => this.updateGradingWeights(selected, gameType));
      })
      .then(() => this.recordSurveyDecision(selected))
      .then(() => this.flipOnboardFlag())
      .then(() => {
        changeLocation('/instructor/welcome');
      })
      .catch(() => {
        this.setState({ submitting: false });
      });
  };

  // PATCH the course so survey_included flips from null to "yes" or
  // "no". The onboarding gate reads this field as its "did the
  // instructor decide?" signal (see onboardingRouting.fetchOnboardingSetup).
  // Non-fatal if it 500s: user can still progress; the gate will just
  // re-prompt on next login.
  recordSurveyDecision = (selected) => {
    const courseId = Number(Auth.getCourseId());
    if (!courseId) return Promise.resolve();
    return patchRequest(`${API}courses/${courseId}/`)
      .send({ survey_included: selected })
      .then(() => {
        // Bust the general-info cache so the next fetchOnboardingSetup
        // (e.g. App.js's per-navigation gate) sees the new value and
        // doesn't yank the instructor back into onboarding.
        clearAppGeneralInfoCache();
      })
      .catch(() => {});
  };

  createSurveys = (gameType) => {
    const courseId = Number(Auth.getCourseId());

    // Default survey windows, measured in days from onboarding time.
    // Instructor can adjust each survey's start/end later on the peer-
    // review admin page.
    //   Initial (Adventurer only): 15 → 22 days
    //   Final / Team Evaluation:   30 → 37 days
    const daysFromNow = (days) => {
      const dt = new Date();
      dt.setDate(dt.getDate() + days);
      return dt;
    };

    // Adventurer runs an Initial and a Final survey; Explorer and
    // Legend run a single end-of-simulation Final survey. `stage` is
    // sent on create so the dashboard's Initial/Final picker can
    // distinguish surveys without falling back to date-sort heuristics.
    const surveyPlan = (gameType === 'Adventurer')
      ? [
          {
            name: 'Initial Team Evaluation',
            stage: 'initial',
            questions: INITIAL_STAGE_QUESTIONS,
            startDays: 15,
            endDays: 22,
          },
          {
            name: 'Final Evaluation',
            stage: 'final',
            questions: FINAL_STAGE_QUESTIONS,
            startDays: 30,
            endDays: 37,
          },
        ]
      : [
          {
            name: 'Team Evaluation',
            stage: 'final',
            questions: FINAL_STAGE_QUESTIONS,
            startDays: 15,
            endDays: 22,
          },
        ];

    const createSurveyOnly = (plan) => postRequest(`${API}v2/forms/surveys/create/`)
      .send({
        name: plan.name,
        course: courseId,
        stage: plan.stage,
        start: daysFromNow(plan.startDays).toISOString(),
        end: daysFromNow(plan.endDays).toISOString(),
        // Students see their own results once the survey closes.
        view_results: true,
      });

    // First: fetch existing surveys for this course and skip creating
    // any planned survey whose name already exists. Guards against
    // duplicate creations when a prior attempt succeeded on survey
    // creation but failed downstream (e.g. gradingweights), and the
    // user retried. Defensive course filter (same reason as
    // updateGradingWeights) rejects rows the BE may return for other
    // courses.
    return getRequest(`${API}v2/forms/surveys/?course=${courseId}`)
      .then(res => {
        const body = res.body || {};
        const raw = Array.isArray(body) ? body : (body.results || []);
        const existing = raw.filter(s => Number(s.course) === Number(courseId));
        const existingNames = new Set(existing.map(s => s.name));
        return surveyPlan.filter(p => !existingNames.has(p.name));
      })
      .catch(() => surveyPlan)
      .then(plansToCreate => {
        if (plansToCreate.length === 0) {
          return [];
        }

        // Fetch all backend questions once, map by normalized text, then
        // create each survey and POST its question set. page_size is padded
        // so paginated list handlers still return everything in one call.
        return getRequest(`${API}v2/forms/questions/?page_size=200`)
          .then(res => {
            const body = res.body || {};
            const allQuestions = Array.isArray(body) ? body : (body.results || []);
            const idByText = {};
            allQuestions.forEach(q => {
              if (q && q.text != null) {
                idByText[normalizeQuestionText(q.text)] = q.id;
              }
            });

            const perSurvey = plansToCreate.map(plan => {
              const questionIds = plan.questions
                .map(t => idByText[normalizeQuestionText(t)])
                .filter(id => id != null);

              return createSurveyOnly(plan).then(surveyRes => {
                const surveyId = surveyRes && surveyRes.body && surveyRes.body.id;
                if (!surveyId || questionIds.length === 0) return surveyRes;

                const questions = questionIds.map((qId, idx) => ({
                  question_id: qId,
                  list_order: idx + 1,
                }));
                return postRequest(`${API}v2/forms/surveys/${surveyId}/questions/`)
                  .send({ questions })
                  .then(() => surveyRes)
                  // Don't fail the onboarding chain if question attach fails.
                  // The survey is created; questions can be added later via
                  // the peer-review admin page.
                  .catch(() => surveyRes);
              });
            });

            return Promise.all(perSurvey);
          })
          .catch(() => {
            // If the questions fetch itself fails, still create the empty
            // surveys so onboarding proceeds. Instructor can attach
            // questions manually later.
            return Promise.all(plansToCreate.map(createSurveyOnly));
          });
      });
  };

  updateGradingWeights = (selected, gameType) => {
    const courseId = Number(Auth.getCourseId());
    const enablePeer = selected === 'yes'
      && (gameType === 'Explorer' || gameType === 'Adventurer');
    const team_grade = enablePeer ? 0.60 : 1.0;
    const peer_weight = enablePeer ? 0.40 : 0;

    return new Promise((resolve, reject) => {
      getRequest(`${API}v2/games/gradingweights/?course=${courseId}`)
        .then(res => {
          const body = res.body || {};
          const raw = Array.isArray(body) ? body : (body.results || []);
          // Defensive filter: BE has been observed returning rows for
          // OTHER courses even when the ?course= filter is applied.
          // Only trust rows whose course field actually matches ours.
          // Prevents onboarding from PATCHing a foreign course's row.
          const results = raw.filter(r => Number(r.course) === Number(courseId));
          const weightsRow = results[0];
          if (!weightsRow || !weightsRow.id) {
            // No existing row — create one with the desired weights.
            // Other grading dimensions default to 0; the instructor can
            // adjust them later on the Set Team Score page.
            postRequest(`${API}v2/games/gradingweights/`)
              .send({
                course: courseId,
                team_grade,
                peer_weight,
                feedback_submission: 0,
                engagement_score: 0,
              })
              .then(() => resolve())
              .catch(err => reject(err));
            return;
          }
          // Send all 4 weight fields so the backend's constraint
          // (peer_weight + team_grade + engagement_score +
          // feedback_submission === 1) holds. A partial PATCH leaves
          // stale non-zero values on the other two fields and
          // triggers a "sum must be 1" non_field_errors response.
          patchRequest(`${API}v2/games/gradingweights/${weightsRow.id}/`)
            .send({
              team_grade,
              peer_weight,
              feedback_submission: 0,
              engagement_score: 0,
            })
            .then(() => resolve())
            .catch(err => reject(err));
        })
        .catch(err => reject(err));
    });
  };

  fetchCourseGameType = () => {
    const courseId = Number(Auth.getCourseId());

    return new Promise((resolve) => {
      getRequest(`${API}courses/${courseId}/`)
        .then(res => {
          const body = res.body || {};
          const raw = body.game_type || null;
          resolve(raw ? raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase() : null);
        })
        .catch(() => resolve(null));
    });
  };

  flipOnboardFlag = () => {
    const userId = Auth.getUserId();
    return new Promise((resolve) => {
      patchRequest(`${API}users/${userId}/`)
        .send({ onboard: 1 })
        .then(() => {
          const user = Auth.getUser();
          if (user) Auth.setUserData({ ...user, onboard: 1 });
          resolve();
        })
        .catch(() => resolve());
    });
  };

  render() {
    const { selected, submitting } = this.state;

    return (
      <OnboardingLayout
        progressStep={3}
        progressTotal={4}
        buttonLabel={submitting ? 'Saving' : 'Continue'}
        buttonDisabled={!selected || submitting}
        onButtonClick={this.handleContinue}
        onBack={() => changeLocation('/instructor/onboarding/simulation-selection')}
      >
        <Heading>Include Team Evaluation?</Heading>
        <InfoBox>
          <strong>Add the Peer Assessment form</strong> to capture student
          reflections and peer feedback. It surfaces valuable insights: each
          student's biggest takeaway, the skills they demonstrated, and a
          teamwork score that factors into their overall grade, reinforcing
          accountability and stronger collaboration.
        </InfoBox>
        <RadioRow
          selected={selected === 'yes'}
          onClick={() => this.handleSelect('yes')}
        >
          <RadioLabel>
            <strong>Yes</strong>
            I want students to complete the Peer Assessment form.
          </RadioLabel>
        </RadioRow>
        <RadioRow
          selected={selected === 'no'}
          onClick={() => this.handleSelect('no')}
        >
          <RadioLabel>
            <strong>No</strong>
            No, I do not want students to complete the Peer Assessment form.
          </RadioLabel>
        </RadioRow>
      </OnboardingLayout>
    );
  }
}

export default TeamEvaluationSurvey;



// WEBPACK FOOTER //
// ./src/instructor/components/onboarding/TeamEvaluationSurvey.js